/**
 * InstructorSelectModal 컴포넌트
 *
 * 강사 선택 모달
 * - 페이지네이션 지원
 * - 검색 기능
 * - 선택 시 콜백 호출
 */

import type { InstructorResponseDto } from '@repo/types';
import React, { useState } from 'react';
import { useInstructors } from '../../hooks/useInstructors';
import Pagination from '../ui/Pagination';

interface InstructorSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (instructorId: number, instructorName: string) => void;
  selectedInstructorId?: number | null;
}

const InstructorSelectModal: React.FC<InstructorSelectModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedInstructorId,
}) => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 10; // 페이지당 10개

  /**
   * 강사 목록 조회
   * - 검색어가 있으면 filterKey="search", filterValue=searchTerm 사용
   */
  const { data: instructorsData, isLoading } = useInstructors({
    page,
    limit,
    sortBy: 'name',
    sortOrder: 'ASC',
    ...(searchTerm && {
      filterKey: 'search',
      filterValue: searchTerm,
    }),
  });

  const instructors = instructorsData?.data || [];
  const meta = instructorsData?.meta;

  /**
   * 검색어 입력 핸들러
   */
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // 검색 시 첫 페이지로 리셋
  };

  /**
   * 강사 선택 핸들러
   */
  const handleSelectInstructor = (instructorId: number, instructorName: string) => {
    onSelect(instructorId, instructorName);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <h2 className="text-xl font-semibold text-slate-800">강사 선택</h2>
          <button onClick={onClose} className="text-slate-400 transition-colors hover:text-slate-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 검색창 */}
        <div className="border-b border-slate-200 p-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="강사 이름으로 검색..."
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
            <button
              type="submit"
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white transition-colors hover:bg-indigo-700">
              검색
            </button>
          </form>
        </div>

        {/* 강사 목록 */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
            </div>
          ) : instructors.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              {searchTerm ? '검색 결과가 없습니다.' : '등록된 강사가 없습니다.'}
            </div>
          ) : (
            <div className="space-y-2">
              {instructors.map((instructor: InstructorResponseDto) => (
                <button
                  key={instructor.id}
                  onClick={() => handleSelectInstructor(instructor.id, instructor.name)}
                  className={`w-full rounded-lg border-2 p-3 text-left transition-all ${
                    selectedInstructorId === instructor.id
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
                  }`}>
                  <div className="flex items-center gap-3">
                    {/* 강사 이미지 */}
                    {instructor.imageUrl ? (
                      <img
                        src={instructor.imageUrl}
                        alt={instructor.name}
                        className="h-12 w-12 flex-shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-slate-200">
                        <span className="text-base text-slate-400">{instructor.name.charAt(0)}</span>
                      </div>
                    )}

                    {/* 강사 정보 */}
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-semibold text-slate-800">{instructor.name}</h3>
                      {instructor.occupation && (
                        <p className="mt-0.5 truncate text-xs text-slate-500">{instructor.occupation}</p>
                      )}
                    </div>

                    {/* 선택 표시 */}
                    {selectedInstructorId === instructor.id && (
                      <div className="flex-shrink-0 text-indigo-600">
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 페이지네이션 */}
        {meta && meta.totalPages > 1 && (
          <div className="border-t border-slate-200 p-4">
            <Pagination currentPage={page} totalPages={meta.totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorSelectModal;
